import React, { ReactNode } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { createUseStyles } from 'react-jss';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import {
  Label,
  FormGroup as PrimitiveFormGroup,
  Col,
} from '@bingads-webui-react/primitive';

import { LineByLineFormGroupStyles } from './ui-next-jss/linebylineformgroup-styles';

const useStyles = createUseStyles(LineByLineFormGroupStyles());

export type ColumnClass = string | string[] | null;

export interface FormGroupProps {
  id?: string;
  label?: string;
  isStatic?: boolean;
  hasError?: boolean;
  showLabel?: boolean;
  labelColClass?: ColumnClass;
  labelColExtraClass?: ColumnClass;
  labelClass?: ColumnClass;
  contentColClass?: ColumnClass;
  contentColExtraClass?: ColumnClass;
  helpPopupTopic?: string | null;
  helpPopupAriaLabel?: string | null;
  helpPopupAriaLabelledBy?: string | null;
  children?: ReactNode;
  flyoutColClass?: ColumnClass;
  flyoutMessageHtml?: string;
  customLabelPrefix?: ReactNode;
  customLabelSuffix?: ReactNode;
  disabled?: boolean;
  className?: string;
  isMuliInputFormGroup?: boolean;
}

export interface InputFormGroupProps extends FormGroupProps {
  errorMessages?: Array<string | null | undefined>;
}

/**
 * FormGroup Props
 * @property {String} id - id of the content in the form goup. Will assign form-group-{uniqueId} if not provided
 * @property {String} label - label of the form group
 * @property {Bool} isStatic - indicate if it's static form group, if so, will add 'form-control-static' to classNames of the content
 * @property {String} labelColClass - provide if you need custom col css class for label.
 *                                    Can be a string or an array of strings
 * @property {String} labelColExtraClass - provide if you need extra col css class for label, it won't override the default `labelColClass`
 *                                    Can be a string or an array of strings
 * @property {String} contentColClass - provide if you need custom col css class for content.
 *                                      Can be a string or an array of strings
 * @property {String} contentColExtraClass - provide if you need extra col css class content, it won't override the default `contentColClass`
 *                                      Can be a string or an array of strings
 * @property {String} hasError - indicate whether to add has-error css class to the formGroup
 * @property {String} helpPopupTopic - will add help popup if provided
 * @property {Object} children - the content
 * @property {Node} customLabelPrefix - the customized label prefix
 * @property {Node} customLabelSuffix - the customized label suffix
 * @property {Bool} disabled - indicate whether the content is disabled
 * @property {String} className - extra className for formGroup
*/

export function FormGroup({
  id,
  label,
  isStatic,
  hasError,
  showLabel,
  labelColClass,
  labelColExtraClass,
  labelClass,
  contentColClass,
  contentColExtraClass,
  helpPopupTopic,
  helpPopupAriaLabel,
  helpPopupAriaLabelledBy,
  children,
  flyoutColClass,
  flyoutMessageHtml,
  customLabelPrefix,
  customLabelSuffix,
  disabled,
  className,
  isMuliInputFormGroup,
}: FormGroupProps) {
  const uniqueId = id || _.uniqueId('form-group-');
  const labelId = `${uniqueId}-label`;

  return (
    <PrimitiveFormGroup className={classNames('form-group', 'row', { 'has-error': hasError }, className)}>
      {showLabel &&
        <Col className={classNames(labelColClass, labelColExtraClass)}>
          {customLabelPrefix}
          <Label className={classNames('item-label', labelClass)} htmlFor={uniqueId}>
            <span id={labelId}>{label}</span>
            {helpPopupTopic &&
              <span>
                <HelpPopup
                  topic={helpPopupTopic}
                  aria-label={helpPopupAriaLabel}
                  aria-labelledby={helpPopupAriaLabelledBy}
                />
              </span>
            }
            {customLabelSuffix}
          </Label>
        </Col>
      }
      {isMuliInputFormGroup && _.isArray(children) && (
        <Col
          role="group"
          aria-labelledby={labelId}
          id={uniqueId}
          className={classNames(contentColClass, contentColExtraClass, 'form-control-container', isStatic && 'form-control-static', disabled && 'form-group-disabled')}
        >
          {_.map(children, (elem: ReactNode, index: number) => (
            <Col className={classNames('col-xs-12', 'form-control-container', isStatic && 'form-control-static')} key={index}>
              {elem}
            </Col>
          ))}
        </Col>
      )}
      {!isMuliInputFormGroup && <Col id={uniqueId} className={classNames(contentColClass, contentColExtraClass, 'form-control-container', isStatic && 'form-control-static', disabled && 'form-group-disabled')}>{children}</Col>}
      {flyoutMessageHtml && (
        <Col className={classNames(flyoutColClass)}>
          <span className="inline-notification-flag" />
          <div
            className="notification-inline"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: flyoutMessageHtml }}
          />
        </Col>
      )}
    </PrimitiveFormGroup>
  );
}

FormGroup.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  isStatic: PropTypes.bool,
  labelColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  labelColExtraClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  labelClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  contentColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  contentColExtraClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  showLabel: PropTypes.bool,
  hasError: PropTypes.bool,
  flyoutMessageHtml: PropTypes.string,
  flyoutColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  customLabelPrefix: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  customLabelSuffix: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isMuliInputFormGroup: PropTypes.bool,
};

FormGroup.defaultProps = {
  id: '',
  label: '',
  isStatic: false,
  labelClass: null,
  labelColClass: 'col-xs-3',
  labelColExtraClass: null,
  contentColClass: 'col-xs-6',
  contentColExtraClass: null,
  helpPopupTopic: null,
  helpPopupAriaLabel: null,
  helpPopupAriaLabelledBy: null,
  showLabel: true,
  hasError: false,
  flyoutColClass: 'col-xs-3',
  flyoutMessageHtml: '',
  customLabelPrefix: null,
  customLabelSuffix: null,
  disabled: false,
  className: '',
  children: null,
  isMuliInputFormGroup: false,
};

export function StaticFormGroup(props: FormGroupProps) {
  return (
    <FormGroup {...props} isStatic />
  );
}

StaticFormGroup.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

StaticFormGroup.defaultProps = {
  id: '',
  label: '',
  helpPopupTopic: null,
  helpPopupAriaLabel: null,
  helpPopupAriaLabelledBy: null,
  children: null,
};

export function InputFormGroup(props: InputFormGroupProps) {
  const errorMessages = _.compact(props.errorMessages);
  const hasError = !_.isEmpty(errorMessages);
  const formGroupProps = _.omit(props, ['errorMessages', 'children']);

  return (
    <FormGroup hasError={hasError} {...formGroupProps}>
      <div className="form-input-container">{props.children}</div>
      {
        hasError && (
          <ul className="help-block error">
            {
              _.map(errorMessages, (errorMessage: string, index: number) => (
                <li key={index}>{errorMessage}</li>
              ))
            }
          </ul>
        )
      }
    </FormGroup>
  );
}

InputFormGroup.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  labelClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  labelColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  contentColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  flyoutColClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  flyoutMessageHtml: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

InputFormGroup.defaultProps = {
  id: '',
  label: '',
  labelColClass: 'col-xs-3',
  labelClass: null,
  contentColClass: 'col-xs-6',
  errorMessages: [],
  flyoutColClass: 'col-xs-3',
  flyoutMessageHtml: '',
  helpPopupTopic: null,
  helpPopupAriaLabel: null,
  helpPopupAriaLabelledBy: null,
  children: null,
};


export function LineByLineFormGroup(props: InputFormGroupProps) {
  return (
    <InputFormGroup {...props} labelColClass="col-xs-12" />
  );
}

LineByLineFormGroup.propTypes = {
  contentColClass: PropTypes.string,
  flyoutColClass: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  labelClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  flyoutMessageHtml: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  showLabel: PropTypes.bool,
};

LineByLineFormGroup.defaultProps = {
  contentColClass: 'col-xs-6',
  flyoutColClass: 'col-xs-3',
  id: '',
  label: '',
  labelClass: null,
  errorMessages: [],
  flyoutMessageHtml: '',
  helpPopupTopic: null,
  helpPopupAriaLabel: null,
  helpPopupAriaLabelledBy: null,
  showLabel: true,
  children: null,
};

export function StyledLineByLineFormGroup(props: InputFormGroupProps) {
  const classes = useStyles();
  return (
    <LineByLineFormGroup {...props} className={classNames(classes.formControl, props.className)} />
  );
}

StyledLineByLineFormGroup.propTypes = {
  contentColClass: PropTypes.string,
  flyoutColClass: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  labelClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  flyoutMessageHtml: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  showLabel: PropTypes.bool,
  className: PropTypes.string,
};

StyledLineByLineFormGroup.defaultProps = {
  contentColClass: 'col-xs-6',
  flyoutColClass: 'col-xs-3',
  id: '',
  label: '',
  labelClass: null,
  errorMessages: [],
  flyoutMessageHtml: '',
  helpPopupTopic: null,
  helpPopupAriaLabel: null,
  helpPopupAriaLabelledBy: null,
  showLabel: true,
  children: null,
  className: '',
};
